import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ROLE_RANK, type Role, type SessionDTO } from '@em/shared';
import { ApiError } from '@em/shared';
import { api, queryClient, SESSION_EXPIRED_EVENT, tokenStore } from '../api';

/** `unreachable`: a token is stored but the server could not be asked about it (restart, network). */
type Status = 'loading' | 'signed_out' | 'signed_in' | 'unreachable';

interface AuthState {
  status: Status;
  session: SessionDTO | null;
  /** Why the user was signed out, shown on the sign-in screen. */
  notice: string | null;
  /** Store a new bearer token and load the session it belongs to. */
  acceptToken: (token: string) => Promise<SessionDTO | null>;
  refresh: () => Promise<SessionDTO | null>;
  signOut: () => Promise<void>;
  /** True when the signed-in user holds at least `min`. */
  can: (min: Role) => boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(tokenStore.get() ? 'loading' : 'signed_out');
  const [session, setSession] = useState<SessionDTO | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setSession(null);
      setStatus('signed_out');
      return null;
    }
    try {
      const s = await api.session();
      setSession(s);
      setStatus('signed_in');
      setNotice(null);
      return s;
    } catch (e) {
      // Only a rejected token signs the user out; a restart or network blip keeps it for a retry.
      if (e instanceof ApiError && e.status === 401) {
        tokenStore.clear();
        setSession(null);
        setStatus('signed_out');
      } else {
        setStatus('unreachable');
      }
      return null;
    }
  }, []);

  const acceptToken = useCallback(
    async (token: string) => {
      tokenStore.set(token);
      return refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* the session may already be gone */
    }
    tokenStore.clear();
    queryClient.clear();
    setSession(null);
    setStatus('signed_out');
    setNotice('You have signed out.');
  }, []);

  useEffect(() => {
    void refresh();
    const onExpired = () => {
      queryClient.clear();
      setSession(null);
      setStatus('signed_out');
      setNotice('Your session has ended. Sessions close after 8 hours without activity; sign in again to continue.');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      session,
      notice,
      acceptToken,
      refresh,
      signOut,
      can: (min) => !!session && ROLE_RANK[session.role] >= ROLE_RANK[min],
    }),
    [status, session, notice, acceptToken, refresh, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const a = useContext(Ctx);
  if (!a) throw new Error('useAuth outside AuthProvider');
  return a;
}

/** The signed-in session (only call inside the authenticated app). */
export function useSession(): SessionDTO {
  const { session } = useAuth();
  if (!session) throw new Error('No session');
  return session;
}
